// The browser-only sign-in ceremonies Unity can't run itself (docs/accounts.md
// "Clients"): passkeys (navigator.credentials), the Google button (Google
// Identity Services) and Sign in with Apple (Apple JS). Unity asks through the
// jslib (HS_AccountCeremony → window.HEAVENSTEEL_HOST.account) and gets the
// answer back as SendMessage("hs_session", "OnPage", json) with the same id.
// Every server call is the documented /v1/auth/* contract (docs/protocol-v3.md
// "Accounts"); tokens only pass through, the page never stores them.

export interface AccountRequest {
	id: string;
	/** passkeyRegister | passkeyLogin | google | apple */
	method: string;
	api: string;
	/** The token the device holds (a guest to upgrade, an account to link to). */
	bearer?: string | null;
	birthYear?: number | null;
	name?: string | null;
	/** google / apple: the OAuth client id from GET /v1/auth/methods. */
	clientId?: string | null;
	/** Resend the last ID token (after the server asked for the birth year). */
	retry?: boolean;
}

export interface AccountResult {
	id: string;
	ok: boolean;
	/** The AuthSession on success. */
	session?: unknown;
	code?: string;
	message?: string;
}

type Json = Record<string, unknown>;

const b64urlToBytes = (s: string): ArrayBuffer => {
	const pad = s.replace(/-/g, "+").replace(/_/g, "/");
	const bin = atob(pad + "===".slice((pad.length + 3) % 4));
	return Uint8Array.from(bin, (c) => c.charCodeAt(0)).buffer as ArrayBuffer;
};
const bytesToB64url = (b: ArrayBuffer | Uint8Array | null): string | null => {
	if (!b) return null;
	const u = b instanceof Uint8Array ? b : new Uint8Array(b);
	let s = "";
	for (const x of u) s += String.fromCharCode(x);
	return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

async function post(
	req: AccountRequest,
	path: string,
	body: Json,
): Promise<{ status: number; json: Json }> {
	const headers: Record<string, string> = {
		"content-type": "application/json",
	};
	if (req.bearer) headers.authorization = `Bearer ${req.bearer}`;
	const res = await fetch(`${req.api}${path}`, {
		method: "POST",
		headers,
		body: JSON.stringify(body),
		mode: "cors",
	});
	let json: Json = {};
	try {
		json = (await res.json()) as Json;
	} catch {}
	return { status: res.status, json };
}

function fail(id: string, json: Json, fallback: string): AccountResult {
	const err = (json.error ?? {}) as { code?: string; message?: string };
	return {
		id,
		ok: false,
		code: err.code ?? "failed",
		message: err.message ?? fallback,
	};
}

const extra = (req: AccountRequest): Json => ({
	...(req.birthYear ? { birthYear: req.birthYear } : {}),
	...(req.name ? { name: req.name } : {}),
});

// ── passkeys ────────────────────────────────────────────────────────────

async function passkey(req: AccountRequest): Promise<AccountResult> {
	if (!window.PublicKeyCredential || !navigator.credentials)
		return {
			id: req.id,
			ok: false,
			code: "unsupported",
			message: "This browser can't use passkeys.",
		};
	const register = req.method === "passkeyRegister";
	const kind = register ? "register" : "login";
	const opt = await post(req, `/v1/auth/passkey/${kind}/options`, {});
	if (opt.status !== 200)
		return fail(req.id, opt.json, "Passkeys aren't available right now.");
	const pk = opt.json.publicKey as Json;
	let cred: PublicKeyCredential | null;
	try {
		if (register) {
			const user = pk.user as Json;
			cred = (await navigator.credentials.create({
				publicKey: {
					...(pk as unknown as PublicKeyCredentialCreationOptions),
					challenge: b64urlToBytes(pk.challenge as string),
					user: {
						...(user as unknown as PublicKeyCredentialUserEntity),
						id: b64urlToBytes(user.id as string),
					},
					excludeCredentials: ((pk.excludeCredentials as Json[]) ?? []).map(
						(c) => ({
							type: "public-key" as const,
							id: b64urlToBytes(c.id as string),
						}),
					),
				},
			})) as PublicKeyCredential | null;
		} else {
			cred = (await navigator.credentials.get({
				publicKey: {
					...(pk as unknown as PublicKeyCredentialRequestOptions),
					challenge: b64urlToBytes(pk.challenge as string),
					allowCredentials: [],
				},
			})) as PublicKeyCredential | null;
		}
	} catch (e) {
		const name = (e as DOMException)?.name;
		return {
			id: req.id,
			ok: false,
			code: name === "NotAllowedError" ? "cancelled" : "passkeyFailed",
			message:
				name === "NotAllowedError"
					? "The passkey request was cancelled."
					: String((e as Error)?.message ?? e),
		};
	}
	if (!cred)
		return {
			id: req.id,
			ok: false,
			code: "cancelled",
			message: "No passkey was chosen.",
		};
	const r = cred.response as AuthenticatorAttestationResponse &
		AuthenticatorAssertionResponse;
	const response: Json = register
		? {
				clientDataJSON: bytesToB64url(r.clientDataJSON),
				attestationObject: bytesToB64url(r.attestationObject),
			}
		: {
				clientDataJSON: bytesToB64url(r.clientDataJSON),
				authenticatorData: bytesToB64url(r.authenticatorData),
				signature: bytesToB64url(r.signature),
				userHandle: bytesToB64url(r.userHandle),
			};
	const credential = {
		id: cred.id,
		rawId: bytesToB64url(cred.rawId),
		type: cred.type,
		response,
	};
	const v = await post(req, `/v1/auth/passkey/${kind}/verify`, {
		challengeId: opt.json.challengeId,
		credential,
		...extra(req),
	});
	return v.status === 200
		? { id: req.id, ok: true, session: v.json }
		: fail(req.id, v.json, "The passkey wasn't accepted.");
}

// ── Google and Apple (OIDC ID tokens) ───────────────────────────────────

let lastIdToken: { provider: string; idToken: string; nonce: string } | null =
	null;

function loadScript(src: string): Promise<void> {
	return new Promise((resolve, reject) => {
		if (document.querySelector(`script[src="${src}"]`)) return resolve();
		const s = document.createElement("script");
		s.src = src;
		s.async = true;
		s.onload = () => resolve();
		s.onerror = () => reject(new Error(`could not load ${src}`));
		document.head.appendChild(s);
	});
}

/** A small modal over the game for a provider's own button (Google requires its rendered button). */
function modal(title: string): {
	box: HTMLElement;
	slot: HTMLElement;
	close: () => void;
	cancelled: Promise<void>;
} {
	const scrim = document.createElement("div");
	scrim.id = "account-modal";
	scrim.style.cssText =
		"position:fixed;inset:0;z-index:50;display:flex;align-items:center;justify-content:center;background:rgba(4,8,14,.72)";
	const box = document.createElement("div");
	box.style.cssText =
		"min-width:300px;max-width:92vw;padding:22px 24px;border-radius:10px;background:#0f1722;color:#e8eef6;font:15px/1.4 Inter,system-ui,sans-serif;box-shadow:0 10px 40px rgba(0,0,0,.5);text-align:center";
	const h = document.createElement("div");
	h.textContent = title;
	h.style.cssText =
		"font:600 17px Saira,Inter,sans-serif;letter-spacing:.06em;margin-bottom:14px";
	const slot = document.createElement("div");
	slot.style.cssText =
		"display:flex;justify-content:center;min-height:44px;margin-bottom:14px";
	const cancel = document.createElement("button");
	cancel.textContent = "Cancel";
	cancel.style.cssText =
		"background:none;border:1px solid #3a4a5e;color:#c9d4e2;border-radius:6px;padding:7px 16px;cursor:pointer";
	box.append(h, slot, cancel);
	scrim.append(box);
	document.body.append(scrim);
	const close = () => scrim.remove();
	const cancelled = new Promise<void>((resolve) => {
		cancel.onclick = () => {
			close();
			resolve();
		};
	});
	return { box, slot, close, cancelled };
}

interface GoogleId {
	accounts: {
		id: {
			initialize(o: Json): void;
			renderButton(el: HTMLElement, o: Json): void;
			cancel(): void;
		};
	};
}
interface AppleId {
	auth: {
		init(o: Json): void;
		signIn(): Promise<{ authorization?: { id_token?: string } }>;
	};
}

async function nonce(req: AccountRequest): Promise<string | null> {
	const n = await post({ ...req, bearer: null }, "/v1/auth/nonce", {});
	return n.status === 200 ? (n.json.nonce as string) : null;
}

async function googleIdToken(
	req: AccountRequest,
	nonceValue: string,
): Promise<string | null> {
	await loadScript("https://accounts.google.com/gsi/client");
	const g = (window as unknown as { google?: GoogleId }).google;
	if (!g) throw new Error("Google sign-in didn't load");
	const m = modal("SIGN IN WITH GOOGLE");
	const token = await Promise.race([
		new Promise<string>((resolve) => {
			g.accounts.id.initialize({
				client_id: req.clientId,
				nonce: nonceValue,
				callback: (r: { credential?: string }) => resolve(r.credential ?? ""),
				ux_mode: "popup",
				itp_support: true,
			});
			g.accounts.id.renderButton(m.slot, {
				theme: "filled_black",
				size: "large",
				text: "continue_with",
				shape: "pill",
			});
		}),
		m.cancelled.then(() => null),
	]);
	m.close();
	return token || null;
}

async function appleIdToken(
	req: AccountRequest,
	nonceValue: string,
): Promise<string | null> {
	await loadScript(
		"https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js",
	);
	const a = (window as unknown as { AppleID?: AppleId }).AppleID;
	if (!a) throw new Error("Sign in with Apple didn't load");
	a.auth.init({
		clientId: req.clientId,
		scope: "email",
		redirectURI: `${location.origin}/play`,
		usePopup: true,
		nonce: nonceValue,
	});
	try {
		const r = await a.auth.signIn();
		return r.authorization?.id_token ?? null;
	} catch {
		return null;
	}
}

async function idToken(req: AccountRequest): Promise<AccountResult> {
	const provider = req.method;
	let token: string | null = null;
	let nonceValue: string | null = null;
	if (req.retry && lastIdToken?.provider === provider) {
		token = lastIdToken.idToken;
		nonceValue = lastIdToken.nonce;
	} else {
		if (!req.clientId)
			return {
				id: req.id,
				ok: false,
				code: "methodDisabled",
				message: `${provider} sign-in isn't switched on.`,
			};
		nonceValue = await nonce(req);
		if (!nonceValue)
			return {
				id: req.id,
				ok: false,
				code: "busy",
				message: "Try again in a moment.",
			};
		token =
			provider === "google"
				? await googleIdToken(req, nonceValue)
				: await appleIdToken(req, nonceValue);
		if (!token)
			return {
				id: req.id,
				ok: false,
				code: "cancelled",
				message: "Sign-in was cancelled.",
			};
		lastIdToken = { provider, idToken: token, nonce: nonceValue };
	}
	const v = await post(req, `/v1/auth/${provider}`, {
		idToken: token,
		nonce: nonceValue,
		...extra(req),
	});
	if (v.status === 200) lastIdToken = null;
	return v.status === 200
		? { id: req.id, ok: true, session: v.json }
		: fail(req.id, v.json, "The sign-in wasn't accepted.");
}

/** window.HEAVENSTEEL_HOST.account: run one ceremony, never throw. */
export async function account(req: AccountRequest): Promise<AccountResult> {
	try {
		if (req.method === "passkeyRegister" || req.method === "passkeyLogin")
			return await passkey(req);
		if (req.method === "google" || req.method === "apple")
			return await idToken(req);
		return {
			id: req.id,
			ok: false,
			code: "badRequest",
			message: `unknown method ${req.method}`,
		};
	} catch (e) {
		return {
			id: req.id,
			ok: false,
			code: "failed",
			message: String((e as Error)?.message ?? e),
		};
	}
}

/** Whether this browser can offer passkeys at all (the button is hidden otherwise). */
export function passkeysSupported(): boolean {
	return (
		typeof window.PublicKeyCredential === "function" && !!navigator.credentials
	);
}
